/* Copyright (c) 2021-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable, NgZone } from '@angular/core';
import { FocusOrigin } from '@angular/cdk/a11y';
import { fromEvent, merge, NEVER, Observable, of, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, mapTo, shareReplay, switchMap, throttleTime } from 'rxjs/operators';
import { Logger, LoggingService } from 'crimson/lib/logging/logging.service';
import { GamepadService } from 'gamepad/lib/gamepad.service';
import { NvInputModality } from './input-modality.type';

interface FocusOriginModality {
    focusOrigin?: FocusOrigin;
    inputModality?: NvInputModality;
}

@Injectable({
    providedIn: 'root'
})

/**
 * Service that detects the input modality (Mouse/Keyboard/Touch/Gamepad).
 */
export class InputModalityService {
    private logger: Logger;
    private focusOriginModalitySubscription: Subscription;
    private focusOriginModality: FocusOriginModality = {};
    private monitoringActivated = false;
    private inputModalityChangedSubject = new Subject<NvInputModality>();

    /**
     * Gets the last recorded focus origin.
     *
     * @returns The last recorded focus origin.
     *
     * @dependency This method requires `startMonitoring` to be called before it can return valid value.
     * The last recorded focus origin is cleared when `stopMonitoring` is called.
     * If called before `startMonitoring` or after `stopMonitoring`, it returns `undefined`.
     */
    get lastFocusOrigin(): FocusOrigin {
        return this.focusOriginModality.focusOrigin;
    }

    /**
     * Gets the last recorded input modality.
     *
     * @returns The last recorded input modality.
     *
     * @dependency This method requires `startMonitoring` to be called before it can return valid value.
     * The last recorded input modality is cleared when `stopMonitoring` is called.
     * If called before `startMonitoring` or after `stopMonitoring`, it returns `undefined`.
     */
    get lastInputModality(): NvInputModality {
        return this.focusOriginModality.inputModality;
    }

    /**
     * Emits when the input modality changes.
     *
     * @note
     * If prefer to avoid unnecessary change detections in component(s), subscribe it outside the angular zone.
     * */
    get inputModalityChanged$(): Observable<NvInputModality> {
        return this.inputModalityChangedSubject.asObservable().pipe(filter(_ => this.monitoringActivated));
    }

    constructor(
        loggingService: LoggingService,
        private gamepadService: GamepadService,
        private ngZone: NgZone
    ) {
        this.logger = loggingService.getLogger('InputModalityService');
        this.ngZone.runOutsideAngular(() => {
            const keyboardPressed$ = fromEvent<KeyboardEvent>(document, 'keydown', { capture: true }).pipe(
                switchMap(event => {
                    // WAR for nvbugs/4046876
                    // This WAR is based on the following assumptions.
                    //   1. 'GoBack' can only be fired by us (e.g. BackNavigationService)
                    //   2. 'GoBack' is only fired on mobile platforms where system back is always on touch screen
                    if (event.key === 'GoBack') {
                        return of(NvInputModality.Touch);
                    }
                    return event.isTrusted ? of(NvInputModality.Keyboard) : NEVER;
                })
            );

            const gamepadInUse$ = this.gamepadService.isAnyGamepadInUse$.pipe(
                filter(gamepadInUse => gamepadInUse === true),
                mapTo(NvInputModality.Gamepad)
            );

            const pointerMove$ = fromEvent<PointerEvent>(document, 'pointermove', { capture: true }).pipe(
                filter(event => event.pointerType === 'mouse'),
                // an arbitary number to prevent excessive emission of this source and slow down the app
                throttleTime(50),
                mapTo(NvInputModality.Mouse)
            );

            const mouseClickedOrTouched$ = fromEvent<PointerEvent>(document, 'pointerdown', { capture: true }).pipe(
                filter(event => event.pointerType === 'mouse' || event.pointerType === 'touch'),
                map(event => {
                    switch (event.pointerType) {
                        case 'mouse':
                            return NvInputModality.Mouse;
                        case 'touch':
                            return NvInputModality.Touch;
                        default:
                            return NvInputModality.Touch; // impossible due to filter above
                    }
                })
            );

            this.updateMonitoringActivationState(true);
            merge(keyboardPressed$, gamepadInUse$, mouseClickedOrTouched$, pointerMove$)
                .pipe(shareReplay(1), distinctUntilChanged())
                .subscribe(value => this.inputModalityChangedSubject.next(value));
        });
    }

    public startMonitoring() {
        this.logger.info('start monitoring');
        this.focusOriginModalitySubscription?.unsubscribe();
        this.focusOriginModalitySubscription = this.ngZone.runOutsideAngular(() =>
            this.inputModalityChanged$
                .pipe(
                    map((inputModality: NvInputModality) => {
                        let focusOrigin: FocusOrigin;
                        switch (inputModality) {
                            case NvInputModality.Keyboard:
                            case NvInputModality.Gamepad:
                                focusOrigin = 'keyboard';
                                break;
                            case NvInputModality.Mouse:
                                focusOrigin = 'mouse';
                                break;
                            case NvInputModality.Touch:
                                focusOrigin = 'touch';
                                break;
                        }
                        return { focusOrigin, inputModality };
                    })
                )
                .subscribe(result => (this.focusOriginModality = result))
        );
    }

    public stopMonitoring() {
        this.logger.info('stop monitoring');

        this.focusOriginModalitySubscription?.unsubscribe();
        this.focusOriginModalitySubscription = undefined;
        this.focusOriginModality = {};
    }

    public updateMonitoringActivationState(enabled: boolean) {
        this.monitoringActivated = enabled;
    }
}
